import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, useTheme, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { usePricing } from '../hooks/usePricing'; // 1. Import the new hook
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase/config';

import SharedHeader from '../components/common/SharedHeader';
import SmartStartHub from '../components/landing/SmartStartHub';
import OutcomeProofSection from '../components/landing/OutcomeProofSection';
import FeaturesSection from '../components/landing/FeaturesSection';
import SwitchingSection from '../components/landing/SwitchingSection';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import DirectorySection from '../components/landing/DirectorySection';
import PricingSection from '../components/landing/PricingSection';
import FooterSection from '../components/landing/FooterSection';
import BackgroundElements from '../components/landing/BackgroundElements';

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Pricing toggles (same as invoicer pattern)
  const [currency, setCurrency] = useState('usd');
  const [isYearly, setIsYearly] = useState(false);

  // Embedded auth state
  const [isTokenAuth, setIsTokenAuth] = useState(false);
  const [tokenAuthError, setTokenAuthError] = useState(null);

  // Check if embedded
  const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === 'true' ||
    window.self !== window.top;

  // Handle embedded authentication (same pattern as Invoicer)
  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = async (event) => {
      // Verify origin is from allowed parent apps (Cloudflare + Firebase fallback)
      const allowedOrigins = [
        'https://connect.futuresenseai.com',
        'http://localhost:3000',
        'http://localhost:5173'
      ];

      const isAllowedOrigin = allowedOrigins.includes(event.origin) ||
        event.origin.includes('connect.futuresenseai.com') ||
        event.origin.includes('aicaller') ||
        event.origin.includes('localhost');

      if (!isAllowedOrigin) return;

      if (event.data?.type === 'FIREBASE_AUTH_TOKEN' && event.data?.token) {
        setIsTokenAuth(true);
        try {
          // Call central API to exchange ID token for custom token
          const response = await fetch(
            'https://us-central1-paymentgateway-ab783.cloudfunctions.net/api/auth/exchange-token',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${event.data.token}`
              }
            }
          );

          if (response.ok) {
            const { customToken } = await response.json();
            await signInWithCustomToken(auth, customToken);
            console.log('Booker: Successfully authenticated via embedded token');
            // Navigate to dashboard after successful auth
            navigate('/dashboard');
          } else {
            console.log('Booker: Token exchange failed, showing error');
            setTokenAuthError('Unable to authenticate. Please open Booker in a new tab.');
          }
        } catch (error) {
          console.error('Booker: Embedded auth error', error);
          setTokenAuthError('Authentication failed. Please try again.');
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Signal to parent that we're ready to receive the token
    if (window.parent !== window) {
      const parentOrigin = document.referrer ? new URL(document.referrer).origin : 'https://connect.futuresenseai.com';
      window.parent.postMessage({ type: 'BOOKER_READY' }, parentOrigin);
      console.log('Booker: Sent BOOKER_READY to parent');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [isEmbedded, navigate]);

  // 2. Call the usePricing hook to get live pricing data
  const { pricing, isLoading: loadingPrices } = usePricing();

  // Show loading state when authenticating via token
  if (isEmbedded && isTokenAuth && !tokenAuthError) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: theme.customEffects?.gradients?.nav || theme.palette.background.default
      }}>
        <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
        <Typography sx={{ color: 'white' }}>Authenticating...</Typography>
      </Box>
    );
  }

  // Show error if token auth failed
  if (isEmbedded && tokenAuthError) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: theme.customEffects?.gradients?.nav || theme.palette.background.default
      }}>
        <Typography sx={{ color: 'error.main', mb: 2 }}>{tokenAuthError}</Typography>
        <Button
          variant="contained"
          onClick={() => window.open('https://booker-1d469.web.app', '_blank', 'noopener,noreferrer')}
        >
          Open Booker in New Tab
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Helmet>
        <title>FutureSense AI — The AI Business Manager for Independent Professionals</title>
        <meta name="description" content="Like hiring a business manager for $30/mo. Books clients, sends reminders, chases payments, and spots churn — so you can focus on your craft." />
        <link rel="canonical" href="https://nexus.futuresenseai.com/" />
        <meta property="og:title" content="FutureSense AI — The AI Business Manager for Independent Professionals" />
        <meta property="og:description" content="Like hiring a business manager for $30/mo. Books clients, sends reminders, chases payments, and spots churn." />
        <meta property="og:url" content="https://nexus.futuresenseai.com/" />
        <meta property="og:image" content="https://nexus.futuresenseai.com/logo512.png" />
      </Helmet>
      <BackgroundElements />
      <SharedHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      {/* Main Content */}
      <SmartStartHub />
      <OutcomeProofSection />
      <FeaturesSection />
      <SwitchingSection />
      <TestimonialsSection />
      <DirectorySection />
      <PricingSection
        pricingData={pricing}
        loadingPrices={loadingPrices}
        currency={currency}
        setCurrency={setCurrency}
        isYearly={isYearly}
        setIsYearly={setIsYearly}
      />
      <FooterSection />
    </Box>
  );
};

export default LandingPage;
